import { useState } from "react";
import { LandingHeader } from "@/components/landing/landing-header";
import { LandingFooter } from "@/components/landing/landing-footer";
import { AuthModal } from "@/components/landing/auth-modal";
import { 
  Clock, 
  BarChart3, 
  FileText, 
  Shield,
  CheckCircle2
} from "lucide-react";

export default function ModelPage() {
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalTab, setAuthModalTab] = useState<"login" | "register">("login");

  const openLogin = () => {
    setAuthModalTab("login");
    setAuthModalOpen(true);
  };

  const openRegister = () => {
    setAuthModalTab("register");
    setAuthModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <LandingHeader onLoginClick={openLogin} onRegisterClick={openRegister} />

      <section className="pt-24 pb-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Modelo Operacional</h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              A HT CAPITAL opera sob um modelo estruturado de avaliação, controle e progressão, 
              baseado em dados objetivos e governança de risco.
            </p>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-16">

            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10">
                  <Clock className="h-6 w-6 text-primary" />
                </div>
                <h2 className="text-2xl font-bold">Apuração End of Day (EOD)</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                A HT CAPITAL adota o regime de apuração End of Day (EOD) como padrão institucional.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Todas as métricas, extratos e avaliações refletem sempre o último pregão oficialmente encerrado.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-card border border-border">
                  <CheckCircle2 className="h-5 w-5 text-primary mb-2" />
                  <h4 className="font-medium mb-1">Consistência Estatística</h4>
                  <p className="text-sm text-muted-foreground">
                    Dados completos e padronizados para análise
                  </p>
                </div>
                <div className="p-4 bg-card border border-border">
                  <CheckCircle2 className="h-5 w-5 text-primary mb-2" />
                  <h4 className="font-medium mb-1">Redução de Ruído</h4>
                  <p className="text-sm text-muted-foreground">
                    Sem decisões emocionais em tempo real
                  </p>
                </div>
                <div className="p-4 bg-card border border-border">
                  <CheckCircle2 className="h-5 w-5 text-primary mb-2" />
                  <h4 className="font-medium mb-1">Governança Técnica</h4>
                  <p className="text-sm text-muted-foreground">
                    Processos auditáveis e transparentes
                  </p>
                </div>
                <div className="p-4 bg-card border border-border">
                  <CheckCircle2 className="h-5 w-5 text-primary mb-2" />
                  <h4 className="font-medium mb-1">Base Histórica Confiável</h4>
                  <p className="text-sm text-muted-foreground">
                    Histórico completo para decisões fundamentadas
                  </p>
                </div>
              </div>

              <div className="p-4 bg-muted/50 border border-border">
                <p className="text-muted-foreground">
                  Não existem métricas parciais, ajustes subjetivos ou avaliações intraday.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-accent/10">
                  <BarChart3 className="h-6 w-6 text-accent" />
                </div>
                <h2 className="text-2xl font-bold">Avaliação e Governança</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                A HT CAPITAL utiliza um sistema proprietário de avaliação comportamental, 
                projetado para medir não apenas resultado, mas principalmente:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { title: "Qualidade de execução", desc: "Precisão e timing das operações" },
                  { title: "Eficiência de captura", desc: "Aproveitamento dos movimentos" },
                  { title: "Exposição adversa", desc: "Controle de risco durante operações" },
                  { title: "Padrão operacional", desc: "Consistência de comportamento" },
                  { title: "Resposta pós-perda", desc: "Recuperação após eventos negativos" },
                  { title: "Aderência ao plano", desc: "Disciplina com o mandato" }
                ].map((item, index) => (
                  <div key={index} className="p-4 bg-card border border-border">
                    <h4 className="font-medium mb-1">{item.title}</h4>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                ))}
              </div>

              <p className="text-muted-foreground">
                Todas as métricas são calculadas automaticamente a partir dos dados oficiais 
                de execução e ficam registradas com trilha completa de auditoria.
              </p>
              <p className="font-medium">
                Cada operador possui histórico técnico rastreável.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-500/10">
                  <FileText className="h-6 w-6 text-yellow-500" />
                </div>
                <h2 className="text-2xl font-bold">Mandato Operacional</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                Cada operador atua sob um mandato operacional individual, versionado e formalizado, 
                que define:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  "Limites de perda diária",
                  "Risco máximo por operação",
                  "Número máximo de operações",
                  "Janelas operacionais autorizadas",
                  "Regras de exposição e conduta",
                  "Parâmetros de segurança"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-2 p-3 bg-card border border-border">
                    <div className="h-2 w-2 rounded-full bg-yellow-500" />
                    {item}
                  </div>
                ))}
              </div>

              <div className="p-4 bg-card border border-yellow-500/30">
                <p className="font-medium">
                  O mandato é a referência soberana da operação. 
                  Qualquer alteração é registrada, versionada e auditável.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500/10">
                  <Shield className="h-6 w-6 text-red-500" />
                </div>
                <h2 className="text-2xl font-bold">Controle de Risco Sistêmico</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                A HT CAPITAL opera com um sistema de proteção de risco em múltiplas camadas, 
                incluindo:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { title: "Limites automáticos", desc: "Bloqueio automático ao atingir limites" },
                  { title: "Lockouts sistêmicos", desc: "Pausa compulsória em situações de risco" },
                  { title: "Regressão de fase", desc: "Retorno de fase por violações graves" },
                  { title: "Intervenções baseadas em dados", desc: "Ações preventivas automáticas" },
                  { title: "Monitoramento comportamental", desc: "Detecção de padrões de risco" }
                ].map((item, index) => (
                  <div key={index} className="p-4 bg-card border border-border">
                    <h4 className="font-medium mb-1">{item.title}</h4>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                ))}
              </div>

              <p className="text-muted-foreground">
                Esses mecanismos existem para proteger tanto o operador quanto a estrutura da mesa.
              </p>
            </div>

          </div>
        </div>
      </section>

      <LandingFooter />

      <AuthModal 
        open={authModalOpen} 
        onOpenChange={setAuthModalOpen}
        defaultTab={authModalTab}
      />
    </div>
  );
}
